import React from 'react';
import Box from '../Box/Box';
import { SxProp } from '../style/sxProp';

export type Props = {
  src?: string;
  size?: 'large' | 'small';
  sx?: SxProp;
  imgSx?: SxProp;
  'aria-hidden'?: boolean;
};

const ProviderLogo: React.FC<React.PropsWithChildren<Props>> = ({
  src,
  sx,
  imgSx,
  size = 'large',
  'aria-hidden': ariaHidden,
}) => (
  <Box
    aria-hidden={ariaHidden}
    sx={{
      ...(size === 'large'
        ? {
            width: '80px',
            minWidth: '80px',
            height: '80px',
            minHeight: '80px',
          }
        : {
            width: '48px',
            minWidth: '48px',
            height: '48px',
            minHeight: '48px',
          }),
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      border: '1px solid',
      borderColor: {
        type: 'theme',
        colorLegacy: 'Neutral/20',
        colorNext: 'neutral/default',
        opacityNext: 0.4,
      },
      borderRadius: '50%',
      overflow: 'hidden',
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ...(sx as any),
    }}
  >
    {src ? (
      <Box
        as="img"
        sx={{
          ...(size === 'large'
            ? {
                width: '80px',
                height: '80px',
              }
            : {
                width: '48px',
                height: '48px',
              }),
          objectFit: 'contain',
          objectPosition: 'center',
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ...(imgSx as any),
        }}
        src={src}
      />
    ) : (
      <Box
        sx={{
          backgroundColor: {
            type: 'theme',
            colorLegacy: 'Neutral/30',
            colorNext: 'neutral/default',
            opacityNext: 0.5,
          },
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          ...(size === 'large'
            ? {
                width: '76px',
                height: '76px',
              }
            : {
                width: '46px',
                height: '46px',
              }),
          borderRadius: '100%',
        }}
      >
        <svg
          width={size === 'large' ? '30' : '16'}
          height={size === 'large' ? '28' : '14'}
          viewBox="0 0 30 28"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M26.4 6.20078H21.9C21.8204 6.20078 21.7441 6.16917 21.6878 6.11291C21.6316 6.05665 21.6 5.98035 21.6 5.90078V5.60078C21.6 4.32774 21.0943 3.10684 20.1941 2.20667C19.2939 1.30649 18.073 0.800781 16.8 0.800781H13.2C11.9269 0.800781 10.706 1.30649 9.80586 2.20667C8.90569 3.10684 8.39997 4.32774 8.39997 5.60078V5.90078C8.39997 5.98035 8.36837 6.05665 8.31211 6.11291C8.25585 6.16917 8.17954 6.20078 8.09997 6.20078H3.59998C2.80433 6.20078 2.04126 6.51685 1.47866 7.07946C0.916046 7.64207 0.599976 8.40513 0.599976 9.20078L0.599976 14.6008C0.599976 15.0782 0.789618 15.536 1.12718 15.8736C1.46475 16.2111 1.92259 16.4008 2.39998 16.4008H12.24C12.2881 16.4 12.3355 16.3889 12.379 16.3682C12.4225 16.3475 12.461 16.3177 12.492 16.2808C12.759 15.8569 13.129 15.5076 13.5676 15.2655C14.0062 15.0234 14.499 14.8964 15 14.8964C15.5009 14.8964 15.9937 15.0234 16.4323 15.2655C16.8709 15.5076 17.241 15.8569 17.508 16.2808C17.5389 16.3177 17.5774 16.3475 17.6209 16.3682C17.6644 16.3889 17.7118 16.4 17.76 16.4008H27.6C28.0774 16.4008 28.5352 16.2111 28.8728 15.8736C29.2103 15.536 29.4 15.0782 29.4 14.6008V9.20078C29.4 8.80681 29.3224 8.41671 29.1716 8.05273C29.0208 7.68875 28.7999 7.35804 28.5213 7.07946C28.2427 6.80088 27.912 6.57991 27.548 6.42914C27.184 6.27838 26.7939 6.20078 26.4 6.20078ZM13.2 3.20078H16.8C17.3805 3.19465 17.9437 3.39919 18.385 3.7765C18.8263 4.15381 19.1158 4.6783 19.2 5.25278C19.2078 5.29563 19.2053 5.33971 19.1928 5.38142C19.1803 5.42313 19.1581 5.46129 19.128 5.49278C19.1001 5.52626 19.0653 5.55326 19.0259 5.57191C18.9865 5.59056 18.9435 5.60041 18.9 5.60078H11.124C11.0804 5.60041 11.0374 5.59056 10.9981 5.57191C10.9587 5.55326 10.9239 5.52626 10.896 5.49278C10.8659 5.46129 10.8437 5.42313 10.8312 5.38142C10.8187 5.33971 10.8162 5.29563 10.824 5.25278C10.9076 4.68242 11.1936 4.16119 11.6299 3.78441C12.0662 3.40762 12.6235 3.20045 13.2 3.20078Z"
            fill="white"
          />
          <path
            d="M14.9998 22.7007C14.5265 22.6629 14.0653 22.5318 13.6428 22.315C13.2203 22.0982 12.8449 21.8 12.5382 21.4375C12.2314 21.075 11.9994 20.6554 11.8556 20.2028C11.7117 19.7503 11.6588 19.2738 11.6998 18.8007C11.6936 18.7088 11.6936 18.6166 11.6998 18.5247C11.7018 18.483 11.6954 18.4413 11.681 18.4022C11.6665 18.363 11.6444 18.3271 11.6158 18.2967C11.5882 18.2669 11.5548 18.243 11.5177 18.2265C11.4806 18.21 11.4405 18.2012 11.3998 18.2007H2.99985C2.85493 18.2017 2.7151 18.2542 2.60522 18.3487C2.49535 18.4431 2.42257 18.5736 2.39985 18.7167C2.32785 19.2207 2.24385 19.7487 2.15985 20.2647C1.73985 22.6647 1.35585 24.9567 2.45985 26.2647C2.76314 26.5919 3.13667 26.8462 3.55236 27.0083C3.96805 27.1704 4.41508 27.2362 4.85984 27.2007H25.1518C25.5966 27.2362 26.0436 27.1704 26.4593 27.0083C26.875 26.8462 27.2485 26.5919 27.5518 26.2647C28.6558 24.9567 28.2718 22.6647 27.8518 20.2647C27.7678 19.7487 27.6838 19.2207 27.6118 18.7167C27.5891 18.5736 27.5163 18.4431 27.4065 18.3487C27.2966 18.2542 27.1568 18.2017 27.0118 18.2007H18.5998C18.5592 18.2012 18.5191 18.21 18.482 18.2265C18.4449 18.243 18.4115 18.2669 18.3838 18.2967C18.3553 18.3271 18.3332 18.363 18.3187 18.4022C18.3043 18.4413 18.2979 18.483 18.2998 18.5247C18.3061 18.6166 18.3061 18.7088 18.2998 18.8007C18.3409 19.2738 18.288 19.7503 18.1441 20.2028C18.0002 20.6554 17.7683 21.075 17.4615 21.4375C17.1548 21.8 16.7794 22.0982 16.3569 22.315C15.9344 22.5318 15.4732 22.6629 14.9998 22.7007Z"
            fill="white"
          />
          <path
            d="M14.9998 20.9007C15.8282 20.9007 16.4998 19.9605 16.4998 18.8007C16.4998 17.6409 15.8282 16.7007 14.9998 16.7007C14.1714 16.7007 13.4998 17.6409 13.4998 18.8007C13.4998 19.9605 14.1714 20.9007 14.9998 20.9007Z"
            fill="white"
          />
        </svg>
      </Box>
    )}
  </Box>
);

export default ProviderLogo;
